import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useLocation } from 'react-router-dom';

interface ActivityEvent {
  event_category: string;
  event_type: string;
  page_path?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

interface ActivityTrackingContextType {
  trackEvent: (category: string, type: string, metadata?: Record<string, any>) => void;
  sessionId: string | null;
  startSession: () => Promise<void>;
  endSession: () => Promise<void>;
}

const ActivityTrackingContext = createContext<ActivityTrackingContextType | undefined>(undefined);

// Device detection utilities
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  
  let browser = 'unknown';
  if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Safari')) browser = 'Safari';
  else if (ua.includes('Edge')) browser = 'Edge';
  
  let os = 'unknown';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  
  return {
    deviceType: isMobile ? 'mobile' : 'desktop',
    browser,
    os,
    isMobile,
  };
};

const generateSessionToken = () => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
};

export function ActivityTrackingProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const eventQueue = useRef<ActivityEvent[]>([]);
  const flushTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionStartTimeRef = useRef<Date | null>(null);
  const lastPageRef = useRef<string | null>(null);

  // Start a new session
  const startSession = useCallback(async () => {
    if (!user) return;
    
    const sessionToken = generateSessionToken();
    const deviceInfo = getDeviceInfo();
    
    try {
      const { data, error } = await supabase
        .from('user_sessions')
        .insert({
          user_id: user.id,
          session_token: sessionToken,
          device_type: deviceInfo.deviceType,
          browser: deviceInfo.browser,
          os: deviceInfo.os,
          is_mobile: deviceInfo.isMobile,
        })
        .select('id')
        .single();
      
      if (error) throw error;
      
      setSessionId(data.id);
      sessionStartTimeRef.current = new Date();
      sessionStorage.setItem('activity_session_id', data.id);
      sessionStorage.setItem('activity_session_start', new Date().toISOString());
    } catch (error) {
      console.error('Failed to start session:', error);
    }
  }, [user]);

  // End the current session
  const endSession = useCallback(async () => {
    const currentSessionId = sessionId || sessionStorage.getItem('activity_session_id');
    if (!currentSessionId) return;
    
    const startTime = sessionStartTimeRef.current || new Date(sessionStorage.getItem('activity_session_start') || Date.now());
    const durationSeconds = Math.floor((Date.now() - startTime.getTime()) / 1000);
    
    try {
      await supabase
        .from('user_sessions')
        .update({
          ended_at: new Date().toISOString(),
          duration_seconds: durationSeconds,
        })
        .eq('id', currentSessionId);
      
      // Flush any remaining events
      await flushEvents();
      
      setSessionId(null);
      sessionStorage.removeItem('activity_session_id');
      sessionStorage.removeItem('activity_session_start');
    } catch (error) {
      console.error('Failed to end session:', error);
    }
  }, [sessionId]);

  // Flush events to database
  const flushEvents = useCallback(async () => {
    if (eventQueue.current.length === 0 || !user) return;
    
    const currentSessionId = sessionId || sessionStorage.getItem('activity_session_id');
    const eventsToFlush = [...eventQueue.current];
    eventQueue.current = [];
    
    try {
      const eventsWithSession = eventsToFlush.map(event => ({
        ...event,
        user_id: user.id,
        session_id: currentSessionId,
      }));
      
      await supabase.from('user_activity_events').insert(eventsWithSession);
    } catch (error) {
      console.error('Failed to flush events:', error);
      // Re-queue failed events
      eventQueue.current = [...eventsToFlush, ...eventQueue.current];
    }
  }, [user, sessionId]);

  // Track an event
  const trackEvent = useCallback((category: string, type: string, metadata?: Record<string, any>) => {
    if (!user) return;
    
    const event: ActivityEvent = {
      event_category: category,
      event_type: type,
      page_path: location.pathname,
      metadata: metadata || {},
      created_at: new Date().toISOString(),
    };
    
    eventQueue.current.push(event);
    
    // Debounce flush - send after 5 seconds of no new events
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
    }
    
    flushTimeoutRef.current = setTimeout(() => {
      flushEvents();
    }, 5000);
    
    // Also flush if we have 10+ events queued
    if (eventQueue.current.length >= 10) {
      flushEvents();
    }
  }, [user, location.pathname, flushEvents]);

  // Track page views on route change
  useEffect(() => {
    if (user && location.pathname !== lastPageRef.current) {
      lastPageRef.current = location.pathname;
      trackEvent('navigation', 'page_view', { path: location.pathname });
    }
  }, [location.pathname, user, trackEvent]);

  // Start session when user logs in
  useEffect(() => {
    if (user && !sessionId && !sessionStorage.getItem('activity_session_id')) {
      startSession();
    }
  }, [user, sessionId, startSession]);

  // Handle page unload - end session
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable session ending
      const currentSessionId = sessionId || sessionStorage.getItem('activity_session_id');
      if (currentSessionId && user) {
        const startTime = sessionStartTimeRef.current || new Date(sessionStorage.getItem('activity_session_start') || Date.now());
        const durationSeconds = Math.floor((Date.now() - startTime.getTime()) / 1000);
        
        // Use navigator.sendBeacon for reliable delivery on page unload
        const payload = JSON.stringify({
          session_id: currentSessionId,
          ended_at: new Date().toISOString(),
          duration_seconds: durationSeconds,
        });
        
        navigator.sendBeacon(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/user_sessions?id=eq.${currentSessionId}`,
          payload
        );
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [sessionId, user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }
      flushEvents();
    };
  }, [flushEvents]);

  return (
    <ActivityTrackingContext.Provider value={{ trackEvent, sessionId, startSession, endSession }}>
      {children}
    </ActivityTrackingContext.Provider>
  );
}

export function useActivityTracking() {
  const context = useContext(ActivityTrackingContext);
  if (context === undefined) {
    throw new Error('useActivityTracking must be used within an ActivityTrackingProvider');
  }
  return context;
}
